import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Trash2 } from 'react-feather';
import { ServerPath } from '../../Constant';
import { _axios } from '../../utils/axios';
import EditIcon from '../../assets/images/svg/editLearning.svg';
import ShareIcon from '../../assets/images/svg/publishLearning.svg';
import Search from '../../assets/images/svg/Search_SoS.svg';
import SearchIcon from '../../assets/images/svg/search_iconSos.svg';
import Filter from '../../assets/images/svg/Filter_SoS.svg';
import CreateQuizManagement from './CreateQuizManagement';
import EditQuizManagement from './EditQuizManagement';
import {
    tableColumns,
    QUIZ_CATEGORY_OPTIONS,
    TOPIC_OPTIONS,
    STATUS_OPTIONS,
    getCategoryLabel,
    getTopicLabel,
    getStatusLabel,
    getStatusColor,
    getApiErrorMessage,
    isNotFoundError
} from './QuizManagementData';

const tableStyles = `
  .rdt_TableCell {
    padding: 4px 2px !important;
  }
  .rdt_TableHead {
    padding: 4px 2px !important;
  }
  .rdt_TableCol {
    padding: 4px 2px !important;
  }
  .rdt_TableCell[data-column-id="status"] {
    position: sticky !important;
    right: 160px !important;
    background-color: white !important;
    z-index: 2 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
  .rdt_TableHeadRow [data-column-id="status"] {
    position: sticky !important;
    right: 160px !important;
    background-color: white !important;
    z-index: 2 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    padding-left: 10px !important;
    padding-right: 0 !important;
  }
  .rdt_TableCell[data-column-id="action"] {
    position: sticky !important;
    right: 0 !important;
    background-color: inherit !important;
    z-index: 1;
  }
  .rdt_TableHeadRow [data-column-id="action"] {
    position: sticky !important;
    right: 0 !important;
    background-color: inherit !important;
    z-index: 1;
  }
  .data-tables {
    overflow-x: auto;
  }
  .btn[title]:hover:after {
    content: attr(title);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    color: white;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 10;
  }
  .btn[title] {
    position: relative;
  }
  .btn-link:hover {
    text-decoration: none !important;
  }
`;

const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

const QuizManagementTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editQuizId, setEditQuizId] = useState(null);

    // Search + filters are all applied server side by GET /quiz-admin
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const searchRef = useRef(null);

    const [showFilters, setShowFilters] = useState(false);
    const filterRef = useRef(null);
    const [filters, setFilters] = useState({ quiz_category: '', topic: '', status: '' });

    const [statusModal, setStatusModal] = useState({ show: false, quiz: null, status: '' });
    const [deleteModal, setDeleteModal] = useState({ show: false, quiz: null });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowSearch(false);
            }
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setShowFilters(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Debounce the search box so we do not fire a request per keystroke.
    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(searchValue.trim());
            setCurrentPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchValue]);

    const handleEdit = (quiz) => {
        setEditQuizId(quiz.quiz_id);
        setShowEdit(true);
    };

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);

            const params = { page: currentPage, limit: perPage };
            if (appliedSearch) params.search = appliedSearch;
            if (filters.quiz_category) params.quiz_category = filters.quiz_category;
            if (filters.topic) params.topic = filters.topic;
            if (filters.status) params.status = filters.status;

            const response = await _axios.get(`${ServerPath}/quiz-admin`, { params });

            if (!response?.data?.success) {
                toast.error(response?.data?.message || 'Failed to fetch quizzes');
                return;
            }

            const transformedData = (response.data.data || []).map(item => {
                const quizId = item.quiz_id ?? item.id;
                const row = {
                    quiz_id: quizId,
                    title: item.title,
                    category: getCategoryLabel(item.quiz_category),
                    // Topic is set independently of the category, so show it whenever it is set.
                    topic: getTopicLabel(item.topic),
                    question_count: item.question_count ?? 0,
                    level_count: item.level_count ?? 0,
                    mapping_count: item.mapping_count ?? 0,
                    created_date: formatDate(item.created_at),
                    status: item.status,
                    raw_data: item
                };

                row.status_component = (
                    <span
                        className="d-inline-block"
                        style={{
                            minWidth: '70px',
                            textAlign: 'center',
                            borderRadius: '20px',
                            padding: '4px 8px',
                            backgroundColor: getStatusColor(item.status),
                            color: '#FFFFFF',
                            fontSize: '10px',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.1)'
                        }}
                    >
                        {getStatusLabel(item.status)}
                    </span>
                );

                row.action = (
                    <div className="d-flex justify-content-center gap-2">
                        <button
                            className="btn btn-link p-0"
                            title="Edit"
                            onClick={(event) => {
                                event.stopPropagation();
                                handleEdit(row);
                            }}
                        >
                            <img src={EditIcon} alt="edit" style={{ width: '24px', height: '24px' }} />
                        </button>
                        <button
                            className="btn btn-link p-0"
                            title="Change status"
                            onClick={(event) => {
                                event.stopPropagation();
                                setStatusModal({ show: true, quiz: row, status: item.status });
                            }}
                        >
                            <img src={ShareIcon} alt="status" style={{ width: '24px', height: '24px' }} />
                        </button>
                        <button
                            className="btn btn-link p-0 text-danger"
                            title="Delete"
                            onClick={(event) => {
                                event.stopPropagation();
                                setDeleteModal({ show: true, quiz: row });
                            }}
                        >
                            <Trash2 size={20} />
                        </button>
                    </div>
                );

                return row;
            });

            setData(transformedData);
            setTotalRows(response.data.total ?? transformedData.length);
        } catch (error) {
            console.error('Error fetching quizzes:', error);
            toast.error(getApiErrorMessage(error, 'Failed to fetch quizzes'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, perPage, appliedSearch, filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({ quiz_category: '', topic: '', status: '' });
        setCurrentPage(1);
    };

    const handleStatusConfirm = async () => {
        const { quiz, status } = statusModal;
        if (!quiz) return;

        try {
            const response = await _axios.patch(`${ServerPath}/quiz-admin/${quiz.quiz_id}/status`, { status });
            if (response?.data?.success) {
                toast.success('Quiz status updated');
                fetchData();
            } else {
                toast.error(response?.data?.message || 'Failed to update the quiz status');
            }
        } catch (error) {
            console.error('Error updating quiz status:', error);
            if (isNotFoundError(error)) {
                toast.error('This quiz no longer exists');
                fetchData();
            } else {
                // e.g. a QUIZ_SECTION quiz without a topic cannot go ACTIVE
                toast.error(getApiErrorMessage(error, 'Failed to update the quiz status'));
            }
        } finally {
            setStatusModal({ show: false, quiz: null, status: '' });
        }
    };

    const handleDeleteConfirm = async () => {
        const { quiz } = deleteModal;
        if (!quiz) return;

        try {
            const response = await _axios.delete(`${ServerPath}/quiz-admin/${quiz.quiz_id}`);
            if (response?.data?.success) {
                toast.success('Quiz deleted');
            } else {
                toast.error(response?.data?.message || 'Failed to delete the quiz');
            }
        } catch (error) {
            console.error('Error deleting quiz:', error);
            if (!isNotFoundError(error)) {
                toast.error(getApiErrorMessage(error, 'Failed to delete the quiz'));
            }
        } finally {
            setDeleteModal({ show: false, quiz: null });
            fetchData();
        }
    };

    const handleCreateClose = (shouldRefresh = false) => {
        setShowCreate(false);
        if (shouldRefresh) fetchData();
    };

    const handleEditClose = (shouldRefresh = false) => {
        setShowEdit(false);
        setEditQuizId(null);
        if (shouldRefresh) fetchData();
    };

    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Container fluid={true}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0">Quiz Management</h3>
                    <div className="d-flex align-items-center gap-3">
                        {/* Search */}
                        <div className="position-relative" ref={searchRef}>
                            <button className="bg-transparent border-0" onClick={() => setShowSearch(true)}>
                                <img src={Search} alt="search" style={{ width: '36px', height: '36px' }} />
                            </button>

                            {showSearch && (
                                <div style={{ position: 'absolute', top: '45px', left: '-180px', width: '220px', zIndex: 1000 }}>
                                    <div
                                        style={{
                                            background: 'white',
                                            borderRadius: '8px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            padding: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <div
                                            style={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                background: '#F8F7FA',
                                                borderRadius: '6px',
                                                padding: '6px 12px'
                                            }}
                                        >
                                            <input
                                                type="text"
                                                placeholder="Search by title..."
                                                value={searchValue}
                                                onChange={(event) => setSearchValue(event.target.value)}
                                                style={{
                                                    width: '100%',
                                                    border: 'none',
                                                    background: 'transparent',
                                                    fontSize: '14px',
                                                    color: '#666',
                                                    outline: 'none'
                                                }}
                                                autoFocus
                                            />
                                            <img src={SearchIcon} alt="search" style={{ width: '20px', height: '20px' }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Filters */}
                        <div className="position-relative" ref={filterRef}>
                            <button className="bg-transparent border-0" onClick={() => setShowFilters(!showFilters)}>
                                <img src={Filter} alt="filter" style={{ width: '36px', height: '36px' }} />
                            </button>

                            {showFilters && (
                                <div
                                    className="position-absolute end-0 mt-2 bg-white rounded shadow p-3"
                                    style={{ width: '240px', zIndex: 1000 }}
                                >
                                    <Form.Group className="mb-2">
                                        <Form.Label style={{ fontSize: '13px', fontWeight: '500' }}>Category</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filters.quiz_category}
                                            onChange={(event) => handleFilterChange('quiz_category', event.target.value)}
                                        >
                                            <option value="">All</option>
                                            {QUIZ_CATEGORY_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-2">
                                        <Form.Label style={{ fontSize: '13px', fontWeight: '500' }}>Topic</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filters.topic}
                                            onChange={(event) => handleFilterChange('topic', event.target.value)}
                                        >
                                            <option value="">All</option>
                                            {TOPIC_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label style={{ fontSize: '13px', fontWeight: '500' }}>Status</Form.Label>
                                        <Form.Select
                                            size="sm"
                                            value={filters.status}
                                            onChange={(event) => handleFilterChange('status', event.target.value)}
                                        >
                                            <option value="">All</option>
                                            {STATUS_OPTIONS.map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>

                                    <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
                                        Clear filters
                                    </Button>
                                </div>
                            )}
                        </div>

                        <Button variant="primary" onClick={() => setShowCreate(true)} className="bg-primary">
                            Create Quiz
                        </Button>
                    </div>
                </div>

                <Row>
                    <Col sm="12">
                        <Card>
                            <CardBody>
                                <DataTable
                                    columns={tableColumns}
                                    data={data}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={(page) => setCurrentPage(page)}
                                    onChangeRowsPerPage={(newPerPage, page) => {
                                        setPerPage(newPerPage);
                                        setCurrentPage(page);
                                    }}
                                    progressPending={loading}
                                    noDataComponent={<div className="p-4 text-muted">No quizzes found</div>}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            <CreateQuizManagement show={showCreate} onHide={handleCreateClose} />
            <EditQuizManagement show={showEdit} onHide={handleEditClose} quizId={editQuizId} />

            {/* Change status */}
            <Modal
                show={statusModal.show}
                onHide={() => setStatusModal({ show: false, quiz: null, status: '' })}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '18px' }}>Change Quiz Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-secondary" style={{ fontSize: '14px' }}>
                        {statusModal.quiz?.title}
                    </p>
                    <Form.Select
                        value={statusModal.status}
                        onChange={(event) => setStatusModal(prev => ({ ...prev, status: event.target.value }))}
                    >
                        {STATUS_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </Form.Select>
                    <div className="text-muted mt-2" style={{ fontSize: '12px' }}>
                        Only Active quizzes are visible in the app.
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        style={{ backgroundColor: '#6366F1' }}
                        onClick={handleStatusConfirm}
                    >
                        Update
                    </Button>
                    <Button variant="danger" onClick={() => setStatusModal({ show: false, quiz: null, status: '' })}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete */}
            <Modal
                show={deleteModal.show}
                onHide={() => setDeleteModal({ show: false, quiz: null })}
                centered
            >
                <Modal.Body className="text-center p-4">
                    <div className="mb-4">
                        <div className="rounded-circle bg-danger bg-opacity-10 d-inline-flex p-4 mb-3">
                            <i className="bi bi-exclamation-lg fs-4 text-danger"></i>
                        </div>
                        <h4>Delete this quiz?</h4>
                        <p className="text-secondary mb-0">
                            "{deleteModal.quiz?.title}" and all of its questions, mappings and users'
                            attempt history will be permanently deleted. This cannot be undone.
                        </p>
                    </div>
                    <div className="d-flex gap-2 justify-content-center">
                        <Button variant="danger" className="px-4" onClick={handleDeleteConfirm}>Delete</Button>
                        <Button variant="secondary" className="px-4" onClick={() => setDeleteModal({ show: false, quiz: null })}>
                            Cancel
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Fragment>
    );
};

export default QuizManagementTable;
